import React, { useContext, useState, useEffect, useRef } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Drawer, Divider, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { OpenInNewOutlined, WarningAmberOutlined, MenuOutlined, CloseOutlined, LogoutOutlined } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { PRIMARY } from '../constants';
import { PreviewCtx } from '../contexts';
import { logoutParticipant } from '../../../services/api';
import { useSite } from '../../../context/SiteContext';

// ─── Auth helpers ─────────────────────────────────────────────────────────────
const getInitials = (user) => {
    if (!user) return '?';
    const first = user.firstName || '';
    const last  = user.lastName  || '';
    if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
    if (first) return first.slice(0, 2).toUpperCase();
    if (user.email) return user.email[0].toUpperCase();
    return '?';
};

const getStoredUser = () => {
    try {
        const raw = localStorage.getItem('govirtual_user') || sessionStorage.getItem('govirtual_user');
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

const isLoggedIn = () =>
    !!(localStorage.getItem('govirtual_token') || sessionStorage.getItem('govirtual_token'));

// ─── Profile Popout ───────────────────────────────────────────────────────────
function ProfilePopout({ user, primary, onLogout }) {
    const initials = getInitials(user);
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || null;

    return (
        <Box sx={{
            position: 'absolute', top: 'calc(100% + 10px)', right: 0,
            width: 260, bgcolor: '#fff', borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 1px 0 rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.07)', overflow: 'hidden', zIndex: 10,
        }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 }}>
                        {initials}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        {fullName && <Typography variant="body2" fontWeight={700} noWrap>{fullName}</Typography>}
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{user.email}</Typography>
                    </Box>
                </Box>
                {user.employeeNumber && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Employee No.</Typography>
                        <Typography variant="caption" fontWeight={600}>{user.employeeNumber}</Typography>
                    </Box>
                )}
            </Box>
            <Box onClick={onLogout} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, cursor: 'pointer', color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' }, transition: 'background 0.15s' }}>
                <LogoutOutlined sx={{ fontSize: 17 }} />
                <Typography variant="body2" fontWeight={600}>Log Out</Typography>
            </Box>
        </Box>
    );
}

// ─── Auth Area (desktop) ──────────────────────────────────────────────────────
function AuthArea({ config, primary, pill = false }) {
    const navigate = useNavigate();
    const { event: siteEvent } = useSite();
    const popoutRef = useRef(null);

    const showLogin = config.showLogin !== false;

    const [loggedIn, setLoggedIn] = useState(isLoggedIn);
    const [user,     setUser]     = useState(getStoredUser);
    const [popoutOpen, setPopoutOpen] = useState(false);

    useEffect(() => {
        const sync = () => { setLoggedIn(isLoggedIn()); setUser(getStoredUser()); };
        window.addEventListener('storage', sync);
        const id = setInterval(sync, 1000);
        return () => { window.removeEventListener('storage', sync); clearInterval(id); };
    }, []);

    useEffect(() => {
        const h = (e) => { if (popoutRef.current && !popoutRef.current.contains(e.target)) setPopoutOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const handleLogout = async () => {
        try { await logoutParticipant(); } catch {}
        ['govirtual_token', 'govirtual_user'].forEach(k => {
            localStorage.removeItem(k);
            sessionStorage.removeItem(k);
        });
        setLoggedIn(false); setUser(null); setPopoutOpen(false);
    };

    const handleLogin = (e) => {
        e.stopPropagation();
        const loginPath = siteEvent?.urlMode === 'flat'
            ? '/login'
            : `/${siteEvent?.slug || ''}/login`;
        navigate(loginPath);
    };

    const handleRegister = (e) => {
        e.stopPropagation();
        navigate(config.registerHref || '/register');
    };

    const resolvedPrimary = primary || config.style?.registerBg || PRIMARY;
    const initials = getInitials(user);

    if (loggedIn && user) {
        return (
            <Box ref={popoutRef} sx={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <Box
                    onClick={() => setPopoutOpen(o => !o)}
                    title={[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                    sx={{
                        width: 34, height: 34, borderRadius: '50%',
                        bgcolor: resolvedPrimary, color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        border: popoutOpen ? `2px solid ${resolvedPrimary}` : '2px solid transparent',
                        outline: popoutOpen ? `2px solid ${resolvedPrimary}30` : 'none',
                        transition: 'all 0.2s',
                        '&:hover': { filter: 'brightness(0.9)' },
                        userSelect: 'none',
                    }}
                >
                    {initials}
                </Box>
                {popoutOpen && <ProfilePopout user={user} primary={resolvedPrimary} onLogout={handleLogout} />}
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {showLogin && (
                <Box onClick={handleLogin} sx={{ px: 2, py: pill ? 0.75 : 0.8, borderRadius: pill ? '999px' : '8px', cursor: 'pointer', border: `1px solid ${alpha('#000', 0.12)}`, transition: 'background 0.15s', '&:hover': { background: alpha('#000', 0.04) } }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: config.style?.linkColor || '#1a1a2e', whiteSpace: 'nowrap' }}>
                        {config.loginLabel || 'Log In'}
                    </Typography>
                </Box>
            )}
            <Box onClick={handleRegister} sx={{ px: 2.5, py: pill ? 0.85 : 0.9, borderRadius: pill ? '999px' : '8px', background: config.style?.registerBg || resolvedPrimary, cursor: 'pointer', transition: 'filter 0.15s', '&:hover': { filter: 'brightness(0.92)' } }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: config.style?.registerColor || '#fff', whiteSpace: 'nowrap' }}>
                    {config.registerLabel || 'Register Now'}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Mobile Auth Area ─────────────────────────────────────────────────────────
function MobileAuthArea({ config, primary }) {
    const navigate = useNavigate();
    const { event: siteEvent } = useSite();
    const showLogin = config.showLogin !== false;
    const [loggedIn, setLoggedIn] = useState(isLoggedIn);
    const [user,     setUser]     = useState(getStoredUser);

    useEffect(() => {
        const sync = () => { setLoggedIn(isLoggedIn()); setUser(getStoredUser()); };
        window.addEventListener('storage', sync);
        const id = setInterval(sync, 1000);
        return () => { window.removeEventListener('storage', sync); clearInterval(id); };
    }, []);

    const handleLogout = async () => {
        try { await logoutParticipant(); } catch {}
        ['govirtual_token', 'govirtual_user'].forEach(k => {
            localStorage.removeItem(k); sessionStorage.removeItem(k);
        });
        setLoggedIn(false); setUser(null);
    };

    const handleLogin = () => {
        const loginPath = siteEvent?.urlMode === 'flat'
            ? '/login'
            : `/${siteEvent?.slug || ''}/login`;
        navigate(loginPath);
    };

    const handleRegister = () => navigate(config.registerHref || '/register');
    const resolvedPrimary = primary || config.style?.registerBg || PRIMARY;

    if (loggedIn && user) {
        const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
        const initials = getInitials(user);
        return (
            <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #f0f2f5', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: resolvedPrimary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                        {initials}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        {fullName && <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }} noWrap>{fullName}</Typography>}
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>{user.email}</Typography>
                    </Box>
                </Box>
                <Box onClick={handleLogout} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#EF4444', py: 0.5, '&:hover': { opacity: 0.8 } }}>
                    <LogoutOutlined sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Log Out</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ px: 2.5, py: 2.5, borderTop: '1px solid #f0f2f5', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {showLogin && (
                <Box onClick={handleLogin} sx={{ py: 1.1, borderRadius: '8px', border: `1px solid ${alpha('#000', 0.12)}`, textAlign: 'center', cursor: 'pointer', '&:hover': { background: alpha('#000', 0.04) }, transition: 'background 0.15s' }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: config.style?.linkColor || '#1a1a2e' }}>
                        {config.loginLabel || 'Log In'}
                    </Typography>
                </Box>
            )}
            <Box onClick={handleRegister} sx={{ py: 1.25, borderRadius: '8px', background: config.style?.registerBg || resolvedPrimary, cursor: 'pointer', textAlign: 'center', '&:hover': { filter: 'brightness(0.92)' }, transition: 'filter 0.15s' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: config.style?.registerColor || '#fff' }}>
                    {config.registerLabel || 'Register Now'}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── External link confirmation dialog ───────────────────────────────────────
function ExternalLinkDialog({ url, open, onConfirm, onCancel }) {
    return (
        <Dialog open={open} onClose={onCancel} onClick={e => e.stopPropagation()}
            PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 24px 64px rgba(0,0,0,0.18)', maxWidth: 400, width: '100%', p: 0.5 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 0.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 2, background: alpha('#f59e0b', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <WarningAmberOutlined sx={{ fontSize: 18, color: '#f59e0b' }} />
                </Box>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' }}>Visit external link?</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 0.5 }}>
                <Typography sx={{ fontSize: '0.82rem', color: '#5a6474', mb: 1 }}>You're about to leave this page and visit:</Typography>
                <Box sx={{ px: 1.5, py: 1, background: '#f0f4f8', borderRadius: 1.5, border: '1px solid #e4e9f0', wordBreak: 'break-all' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: '#1075BA' }}>{url}</Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2 }}>
                <Button onClick={onCancel} variant="outlined" size="small"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e4e9f0', color: '#5a6474', '&:hover': { borderColor: '#c4cdd8', background: '#f8f9fb' } }}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} variant="contained" size="small" startIcon={<OpenInNewOutlined sx={{ fontSize: 14 }} />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: PRIMARY, boxShadow: 'none', '&:hover': { background: '#0d62a0', boxShadow: 'none' } }}>
                    Visit link
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ─── Nav link ─────────────────────────────────────────────────────────────────
function NavLink({ link, color, onExternalConfirm, mobile = false }) {
    const type     = link.type || 'external';
    const href     = link.href || '';
    const navigate = useNavigate();
    const location = useLocation();
    const { event } = useSite();

    const buildPagePath = (pageSlug) => {
        if (!pageSlug) return event?.urlMode === 'flat' ? '/' : `/${event?.slug || ''}`;
        return event?.urlMode === 'flat' ? `/${pageSlug}` : `/${event?.slug || ''}/${pageSlug}`;
    };

    const scrollToSection = (sectionId) => {
        const el = document.getElementById(`section-${sectionId}`)
            || document.querySelector(`[data-section-id="${sectionId}"]`);
        if (!el) return false;
        let scrollParent = el.parentElement;
        while (scrollParent && scrollParent !== document.body) {
            const { overflow, overflowY } = window.getComputedStyle(scrollParent);
            if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) break;
            scrollParent = scrollParent.parentElement;
        }
        if (scrollParent && scrollParent !== document.body) {
            const parentRect = scrollParent.getBoundingClientRect();
            const elRect     = el.getBoundingClientRect();
            scrollParent.scrollBy({ top: elRect.top - parentRect.top - 16, behavior: 'smooth' });
        } else {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return true;
    };

    const handleClick = (e) => {
        e.stopPropagation();

        if (type === 'section') {
            // href may be "/event-slug/page#section-abc" or just "/event-slug/page"
            const [pagePart, hashPart] = href.split('#');
            const sectionId = hashPart?.replace('section-', '');
            const currentPath = location.pathname;
            const isSamePage = !pagePart || currentPath === pagePart;

            if (isSamePage) {
                scrollToSection(sectionId);
            } else {
                navigate(pagePart, { state: { scrollToSection: sectionId } });
            }
            return;
        }

        if (type === 'page') {
            navigate(href);
            return;
        }

        if (href) onExternalConfirm(href, !!link.newTab);
    };

    return (
        <Typography onClick={handleClick} sx={{
            fontSize: mobile ? '1rem' : '0.875rem',
            fontWeight: 500,
            color: color || '#1a1a2e',
            cursor: 'pointer',
            whiteSpace: mobile ? 'normal' : 'nowrap',
            transition: 'color 0.15s',
            py: mobile ? 0.5 : 0,
            '&:hover': { color: PRIMARY, textDecoration: 'underline' },
        }}>
            {link.label || 'Link'}
        </Typography>
    );
}

// ─── Logo ─────────────────────────────────────────────────────────────────────
function LogoArea({ config, onLogoClick }) {
    const clickSx = onLogoClick ? { cursor: 'pointer', '&:hover': { opacity: 0.8 }, transition: 'opacity 0.15s' } : {};
    if (config.logoSrc) {
        return (
            <Box onClick={onLogoClick} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ...clickSx }}>
                <Box component="img" src={config.logoSrc} alt={config.logoAlt || 'Logo'}
                    sx={{ height: config.logoHeight || 36, width: 'auto', display: 'block', objectFit: 'contain' }} />
            </Box>
        );
    }
    return (
        <Box onClick={onLogoClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ...clickSx }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: alpha(PRIMARY, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: PRIMARY }} />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: PRIMARY, letterSpacing: '-0.5px' }}>
                {config.logoText || 'Event'}
            </Typography>
        </Box>
    );
}

// ─── Mobile side drawer ───────────────────────────────────────────────────────
function MobileDrawer({ open, onClose, config, onExternalConfirm, isBuilderMode, primary, onLogoClick }) {
    const links = config.links || [];

    const navLinks = links.length > 0 && links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} onExternalConfirm={onExternalConfirm} mobile />);

    if (isBuilderMode) {
        if (!open) return null;
        return (
            <Box onClick={e => e.stopPropagation()} sx={{
                position: 'absolute', top: 0, right: 0, width: 240, maxHeight: 600,
                background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.14)',
                zIndex: 999, display: 'flex', flexDirection: 'column',
                borderLeft: '1px solid #e8edf2',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #f0f2f5' }}>
                    <LogoArea config={config} onLogoClick={onLogoClick} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#5a6474' }}>
                        <CloseOutlined sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {navLinks}
                </Box>
                <MobileAuthArea config={config} primary={primary} />
            </Box>
        );
    }

    return (
        <Drawer anchor="right" open={open} onClose={onClose}
            PaperProps={{ sx: { width: 280, display: 'flex', flexDirection: 'column' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #f0f2f5' }}>
                <LogoArea config={config} onLogoClick={onLogoClick} />
                <IconButton size="small" onClick={onClose} sx={{ color: '#5a6474' }}>
                    <CloseOutlined sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {navLinks}
            </Box>
            <Divider />
            <MobileAuthArea config={config} primary={primary} />
        </Drawer>
    );
}

// ─── Mobile header bar ────────────────────────────────────────────────────────
function MobileBar({ config, isBuilderMode, onExternalConfirm, pill = false, primary, onLogoClick }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const bg = pill ? (config.style?.pillBg || '#ffffff') : (config.style?.background || '#ffffff');
    const borderBottom = pill ? 'none' : (config.style?.borderBottom || `1px solid ${alpha('#000', 0.08)}`);

    const bar = (
        <Box sx={{
            width: '100%', background: bg, borderBottom,
            px: '20px', height: pill ? 56 : 60,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxSizing: 'border-box',
            borderRadius: pill ? '999px' : 0,
            boxShadow: pill ? '0 4px 24px rgba(0,0,0,0.10)' : 'none',
            border: pill ? `1px solid ${alpha('#000', 0.07)}` : 'none',
            position: 'relative',
        }}>
            <LogoArea config={config} onLogoClick={onLogoClick} />
            <Box
                onClick={e => { e.stopPropagation(); setDrawerOpen(true); }}
                sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#5a6474',
                    border: '1px solid #e4e9f0', transition: 'all 0.15s',
                    '&:hover': { background: alpha(PRIMARY, 0.06), borderColor: alpha(PRIMARY, 0.3), color: PRIMARY },
                }}
            >
                <MenuOutlined sx={{ fontSize: 20 }} />
            </Box>
            <MobileDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                config={config}
                onExternalConfirm={onExternalConfirm}
                isBuilderMode={isBuilderMode}
                primary={primary}
                onLogoClick={onLogoClick}
            />
        </Box>
    );

    if (!pill) return bar;
    return (
        <Box sx={{ width: '100%', px: '16px', pt: `${config.style?.pillTop ?? 16}px`, pb: '8px', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
            {bar}
        </Box>
    );
}

// ─── Desktop: Classic full-width bar ─────────────────────────────────────────
function ClassicHeader({ config, onExternalConfirm, primary, isPreview, onLogoClick, isSticky }) {
    const links = config.links || [];
    return (
        <Box sx={{
            width: '100%',
            background: config.style?.background || '#ffffff',
            borderBottom: config.style?.borderBottom || `1px solid ${alpha('#000', 0.08)}`,
            px: config.style?.paddingX || '48px',
            height: config.style?.height || 72,
            mb: -10,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 2, boxSizing: 'border-box',
        }}>
            <LogoArea config={config} onLogoClick={onLogoClick} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1, justifyContent: 'center' }}>
                {links.length > 0 && links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} onExternalConfirm={onExternalConfirm} />)}
            </Box>
            {isPreview
                ? <AuthArea config={config} primary={primary} pill={false} />
                : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: config.style?.loginColor || PRIMARY, cursor: 'default', whiteSpace: 'nowrap' }}>
                            {config.loginLabel || 'Login'}
                        </Typography>
                        <Box sx={{ px: 2.5, py: 0.9, borderRadius: '8px', background: config.style?.registerBg || PRIMARY, cursor: 'default' }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: config.style?.registerColor || '#fff', whiteSpace: 'nowrap' }}>
                                {config.registerLabel || 'Register Now'}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        </Box>
    );
}

// ─── Desktop: Pill header ─────────────────────────────────────────────────────
function PillHeader({ config, onExternalConfirm, primary, isPreview, onLogoClick }) {
    const links     = config.links || [];
    const paddingX  = config.style?.paddingX || '40px';
    const maxWidth  = config.style?.pillMaxWidth || 1100;
    const topOffset = config.style?.pillTop ?? 16;

    return (
        <Box sx={{ width: '100%', px: paddingX, pt: `${topOffset + 8}px`, pb: `${topOffset + 8}px`, display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: config.style?.pillBg || '#ffffff',
                borderRadius: '999px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                px: 2.5, height: 56, gap: 2,
                width: '100%', maxWidth: maxWidth,
                boxSizing: 'border-box',
                border: `1px solid ${alpha('#000', 0.07)}`,
            }}>
                <LogoArea config={config} onLogoClick={onLogoClick} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, justifyContent: 'center' }}>
                    {links.length > 0 && links.map((link, i) => <NavLink key={link.id || i} link={link} color={config.style?.linkColor} onExternalConfirm={onExternalConfirm} />)}
                </Box>
                {isPreview
                    ? <AuthArea config={config} primary={primary} pill={true} />
                    : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: config.style?.loginColor || PRIMARY, cursor: 'default', whiteSpace: 'nowrap' }}>
                                {config.loginLabel || 'Login'}
                            </Typography>
                            <Box sx={{ px: 2.5, py: 0.85, borderRadius: '999px', background: config.style?.registerBg || PRIMARY, cursor: 'default' }}>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: config.style?.registerColor || '#fff', whiteSpace: 'nowrap' }}>
                                    {config.registerLabel || 'Register Now'}
                                </Typography>
                            </Box>
                        </Box>
                    )
                }
            </Box>
        </Box>
    );
}

// ─── Chip label ───────────────────────────────────────────────────────────────
function ChipLabel({ selected, pill, mobile }) {
    const text = [pill ? 'Header · Pill' : 'Header', mobile ? '· Mobile' : ''].filter(Boolean).join(' ');
    return (
        <Box sx={{
            position: 'absolute', top: 2, left: 2, zIndex: 20,
            px: 0.9, py: 0.2,
            background: selected ? PRIMARY : alpha(PRIMARY, 0.12),
            borderRadius: '4px 0 4px 0',
            fontSize: '0.58rem', fontWeight: 700,
            color: selected ? '#fff' : PRIMARY,
            letterSpacing: '0.5px', textTransform: 'uppercase',
            pointerEvents: 'none', transition: 'all 0.15s',
        }}>
            {text}
        </Box>
    );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export function HeaderSection({ section, config: configProp, selected, onSelectHeader, breakpoint = 'desktop' }) {
    const isPreview = useContext(PreviewCtx);
    const config    = configProp || section.headerConfig || {};
    const isPill    = (config.template || 'classic') === 'pill';
    const isSticky  = config.sticky === true;
    const isMobile  = breakpoint === 'mobile';
    const isBuilderMode = !isPreview;

    const primary = config.style?.primaryColor || PRIMARY;

    const navigate = useNavigate();
    const { event } = useSite();
    const onLogoClick = isPreview ? () => {
        const homePath = event?.urlMode === 'flat' ? '/' : `/${event?.slug || ''}`;
        navigate(homePath);
    } : undefined;

    const [externalDialog, setExternalDialog] = useState(null);
    const handleExternalConfirm = (url, newTab) => setExternalDialog({ url, newTab });
    const confirmVisit = () => {
        if (externalDialog?.url) window.open(externalDialog.url, externalDialog.newTab ? '_blank' : '_self', 'noopener,noreferrer');
        setExternalDialog(null);
    };

    const inner = isMobile
        ? <MobileBar config={config} isBuilderMode={isBuilderMode} onExternalConfirm={handleExternalConfirm} pill={isPill} primary={primary} onLogoClick={onLogoClick} />
        : isPill
            ? <PillHeader config={config} onExternalConfirm={handleExternalConfirm} primary={primary} isPreview={isPreview} onLogoClick={onLogoClick} />
            : <ClassicHeader config={config} onExternalConfirm={handleExternalConfirm} primary={primary} isPreview={isPreview} onLogoClick={onLogoClick} isSticky={isSticky}/>;

    const stickySpacerHeight = (() => {
        if (isPill) {
            const topOffset = config.style?.pillTop ?? 16;
            return topOffset + 56 + 8;
        }
        return config.style?.height || 72;
    })();

    // ── Preview / live mode ────────────────────────────────────────────────────
    if (isPreview) {
        const wrapSx = isPill
            ? { position: isSticky ? 'sticky' : 'relative', top: 0, height: 0, overflow: 'visible', mb: isSticky ? 0 : `0px`, zIndex: isSticky ? 200 : 10 }
            : { position: isSticky ? 'sticky' : 'relative', top: 0, zIndex: isSticky ? 200 : 'auto', zIndex: 100 };

        return (
            <>
                <Box sx={wrapSx}>
                    {isPill
                        ? <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>{inner}</Box>
                        : inner
                    }
                </Box>
                <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
            </>
        );
    }

    // ── Builder mode ───────────────────────────────────────────────────────────
    const selectionSx = {
        outline: selected ? `2px solid ${PRIMARY}` : '2px solid transparent',
        outlineOffset: -2,
        transition: 'outline-color 0.15s',
        '&:hover': { outline: `2px solid ${selected ? PRIMARY : alpha(PRIMARY, 0.45)}` },
    };

    const onClick = (e) => { e.stopPropagation(); onSelectHeader(section.id); };

    if (isPill) {
        return (
            <>
                <Box onClick={onClick} sx={{
                    position: isSticky ? 'sticky' : 'relative',
                    top: 0, height: 0, overflow: 'visible',
                    mb: isSticky ? 0 : `-${stickySpacerHeight}px`,
                    zIndex: isSticky ? 200 : 10, cursor: 'pointer',
                }}>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, ...selectionSx, cursor: 'pointer', userSelect: 'none' }}>
                        {inner}
                        <ChipLabel selected={selected} pill mobile={isMobile} />
                    </Box>
                </Box>
                <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
            </>
        );
    }

    return (
        <>
            <Box onClick={onClick} sx={{
                position: isSticky ? 'sticky' : 'relative',
                top: 0, zIndex: isSticky ? 200 : 'auto',
                cursor: 'pointer', userSelect: 'none', ...selectionSx,
            }}>
                {inner}
                <ChipLabel selected={selected} pill={false} mobile={isMobile} />
            </Box>
            <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
        </>
    );
}